import { colors } from '@sweep-io/sweep-design';
import useConfirm from '../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import ConfirmDestructiveDialog from '../common/dialogs/ConfirmDestructiveDialog';
import { ActionsMenuItem } from '../common/actions-menu/ActionsMenuItem';
import { useState } from 'react';
import { useFeatureToggle } from '../../feature-flags/useFeatureToggle';
import useSendBiEvent from '../../hooks/useSendBiEvent';
import { AI_AGENTS_EVENTS } from '../../services/events';

const ChatMenuActions = ({
  closeMenu,
  onRename,
  onDelete,
  togglePin,
  isStarred,
  onExportToPdf,
  onShare,
}: {
  onRename: () => void;
  onDelete: () => void;
  closeMenu: () => void;
  togglePin: () => void;
  isStarred: boolean;
  onExportToPdf: () => Promise<boolean>;
  onShare: () => void;
}) => {
  const { openConfirm, onCancel, onConfirm } = useConfirm();
  const [isExporting, setIsExporting] = useState(false);
  const { artifacts: artifactsFeatureFlag } = useFeatureToggle();
  const sendBiEvent = useSendBiEvent();

  const onDeleteClick = async () => {
    const deleteMessage = artifactsFeatureFlag
      ? 'Deleting this chat will also remove any artifacts associated with it. This action cannot be undone.'
      : "You'll no longer see this chat here";

    const isConfirmed = await openConfirm(
      <ConfirmDestructiveDialog
        open
        onConfirm={onConfirm}
        onClose={onCancel}
        title="Are you sure you want to delete this chat?"
        confirmText="Delete"
        cancelText="Cancel"
      >
        {deleteMessage}
      </ConfirmDestructiveDialog>,
    );
    if (isConfirmed) {
      onDelete();
    }
  };

  const handleExportToPdf = async () => {
    if (!onExportToPdf) return;
    sendBiEvent({ name: AI_AGENTS_EVENTS.exportToPdfFromMenu });

    setIsExporting(true);
    try {
      await onExportToPdf();
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <ActionsMenuItem
        action={{
          label: isStarred ? 'Unstar' : 'Star',
          value: isStarred ? 'Unstar' : 'Star',
        }}
        handleClose={closeMenu}
        onClick={togglePin}
        key="Pin"
      />
      <ActionsMenuItem
        action={{
          label: 'Rename',
          value: 'Rename',
        }}
        handleClose={closeMenu}
        onClick={onRename}
        key="Rename"
      />
      <ActionsMenuItem
        action={{
          label: 'Export to PDF',
          value: 'Export to PDF',
          disabled: isExporting,
        }}
        handleClose={closeMenu}
        onClick={handleExportToPdf}
        key="ExportToPdf"
      />
      <ActionsMenuItem
        action={{
          label: 'Share',
          value: 'Share',
        }}
        handleClose={closeMenu}
        onClick={onShare}
        key="Share"
      />
      <ActionsMenuItem
        action={{
          label: 'Delete',
          value: 'Delete',
          actionSx: { color: colors.blush[600] },
        }}
        handleClose={closeMenu}
        onClick={onDeleteClick}
        key="Delete"
      />
    </>
  );
};
export default ChatMenuActions;
